import React from 'react';
import { Post, UserData, Community } from '../types';
import { Capacitor } from '@capacitor/core';

export const canViewPost = (post: Post, currentUser: string | null, usersData: Record<string, UserData>, communities: Community[], isDirectLink: boolean = false): boolean => {
    // Check for blocks first (mutual block)
    if (currentUser && usersData[currentUser]?.blockedUsers?.includes(post.author)) return false;
    if (currentUser && usersData[post.author]?.blockedUsers?.includes(currentUser)) return false;

    // If the post has been shared by the author and is accessed via direct link, allow access
    if (isDirectLink && post.sharedByAuthor) return true;

    // Footprint posts are always public
    if (post.footprintDetails) return true;

    // Public non-community posts are visible to everyone (not blocked)
    if (post.visibility === 'public' && !post.communityId) return true;

    // Community post logic
    if (post.communityId) {
        const community = communities.find(c => c._id === post.communityId);
        if (!community) return false; // If community doesn't exist anymore, hide post
        
        if (!community.isPrivate) return true; // Public community posts are visible
        
        // Private community: check membership
        if (!currentUser) return false;
        return community.members.includes(currentUser);
    }

    // Private/Followers/Groups logic (non-community)
    if (!currentUser) return false; // Guests can't see non-public posts
    if (post.author === currentUser) return true; // Author can see their own post

    const authorData = usersData[post.author];
    if (!authorData) return false;

    // Group visibility logic
    if (post.visibility === 'groups') {
        const userGroups = authorData.followerGroups || [];
        const allowedGroupIds = post.visibilityGroups || [];
        // Check if user is in any of the allowed groups
        return userGroups.some(g => allowedGroupIds.includes(g.id) && g.followers.includes(currentUser));
    }

    // Followers/Private logic: only followers can see them
    return (authorData.followers || []).includes(currentUser);
};

export const parseHashtags = (text: string): string[] => {
    const hashtagRegex = /#([a-zA-Z0-9_\-]+)/gi;
    const matches = text.match(hashtagRegex);
    return matches ? matches.map(m => m.substring(1).toLowerCase()) : [];
};

export const formatTime = (time: number) => {
    if (isNaN(time) || time === Infinity) {
        return '00:00';
    }
    const minutes = Math.floor(time / 60).toString().padStart(2, '0');
    const seconds = Math.floor(time % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
};

/**
 * Hardened slider guard that strictly enforces drag-only interaction.
 * If the interaction doesn't start directly on the thumb, it prevents the default
 * jump-to-value behavior using a combination of preventDefault and a temporary disable.
 */
export const handleSliderPointerDown = (e: React.PointerEvent<HTMLInputElement>, currentValue: number, min: number, max: number) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (currentValue - min) / (max - min);
    
    // Standardize hit detection: Use 12px thumb assumption (range-sm) but a generous 24px radius
    // for hit detection to accommodate 16px thumbs and browser variations.
    const thumbWidth = 12; 
    const usableWidth = rect.width - thumbWidth;
    const thumbX = rect.left + (thumbWidth / 2) + (percent * usableWidth);
    
    // Check if the pointer is within a generous reach of the thumb
    const isNearThumb = Math.abs(e.clientX - thumbX) <= 24;

    if (!isNearThumb) {
        // Stop the browser from starting the interaction
        e.preventDefault();
        e.stopPropagation();
        
        // Force the input to lose focus and temporarily disable it
        // to kill any pending browser-level click/tap handling.
        const target = e.currentTarget;
        const originalDisabled = target.disabled;
        target.disabled = true;
        target.blur();
        
        // Restore after a short delay
        setTimeout(() => {
            if (target) {
                target.disabled = originalDisabled;
            }
        }, 30);
    }
};

/**
 * Universal copy-to-clipboard utility with fallback for non-secure contexts and Cordova
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
    // 00. Try Capacitor Clipboard for native apps
    if (Capacitor.isNativePlatform()) {
        try {
            const { Clipboard } = await import('@capacitor/clipboard');
            await Clipboard.write({ string: text });
            return true;
        } catch (e) {
            console.error('Capacitor clipboard error:', e);
        }
    }

    // 0. Try Cordova Plugin for mobile
    const cordova = (window as any).cordova;
    if (cordova?.plugins?.clipboard) {
        return new Promise((resolve) => {
            cordova.plugins.clipboard.copy(text, () => resolve(true), () => resolve(false));
        });
    }

    // 1. Try modern navigator.clipboard API
    if (navigator?.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch (err) {
            console.error('Modern clipboard error:', err);
        }
    }

    // 2. Fallback to hidden textarea + execCommand('copy')
    try {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        
        // Ensure it's not visible and doesn't scroll the page
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        textArea.style.top = "0";
        document.body.appendChild(textArea);
        
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        return successful;
    } catch (err) {
        console.error('Fallback clipboard error:', err);
        return false;
    }
};

/**
 * Safe read-from-clipboard utility
 */
export const readFromClipboard = async (): Promise<string | null> => {
    // 0. Try Cordova Plugin for mobile
    const cordova = (window as any).cordova;
    if (cordova?.plugins?.clipboard) {
        return new Promise((resolve) => {
            cordova.plugins.clipboard.paste((text: string) => resolve(text), () => resolve(null));
        });
    }

    if (navigator?.clipboard?.readText) {
        try {
            return await navigator.clipboard.readText();
        } catch (err) {
            console.error('Modern clipboard read error:', err);
        }
    }
    
    // There is no standard web fallback for reading clipboard without user interaction
    // like a paste event on an input. In those cases, we return null and let the 
    // UI handle it (e.g. by not showing the paste button).
    return null;
};

/**
 * Normalizes various postcard shapes (including legacy variants) 
 * into the three standard display modes: wide, tall, and square.
 */
export const getNormalizedPostcardShape = (shape?: string, postType?: string, firstMedia?: { width?: number; height?: number; domain?: string }, textContent?: string, textSize?: string, columns?: number): 'wide' | 'tall' | 'square' | 'wide_tall' => {
    // 0. Explicit shape overrides for specific media domains that must dictate shape
    if (firstMedia) {
        const domain = (firstMedia as any).domain;
        const isYouTubeShort = (firstMedia as any).isYouTubeShort === true || 
                               (typeof (firstMedia as any).url === 'string' && (firstMedia as any).url.includes('/shorts/')) ||
                               (typeof (firstMedia as any).embedHtml === 'string' && (firstMedia as any).embedHtml.includes('/shorts/'));
                               
        if (domain === 'tiktok.com' ||
            domain === 'instagram.com' ||
            domain === 'facebook.com' ||
            domain === 'fb.watch' ||
            isYouTubeShort
        ) {
            return 'tall';
        }
        
        if (domain === 'youtube.com' || domain === 'youtu.be') {
            return 'wide';
        }
    }

    // 1. For text posts, use line estimation to fill ~90% of vertical space before expanding
    if (postType === 'text') {
        const text = (textContent || '').trim();
        const length = text.length;
        // Count explicit newlines to factor into vertical height
        const newlines = text.split('\n').length - 1;
        
        // Average characters per line in a 2-column WIDE postcard
        let charsPerLineWide = 28; 
        if (textSize === 'lg') charsPerLineWide = 20;
        if (textSize === 'sm') charsPerLineWide = 36;
        if (textSize === 'xs') charsPerLineWide = 45;

        const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;

        if (isDesktop) {
            // Desktop defaults to square. Only expands to wide if it exceeds 90% of square vertical space.
            // There should NEVER be wide_tall posts on desktop.
            const estimatedLinesSquare = (length / (charsPerLineWide / 2)) + newlines;
            const maxLinesBeforeExtend = 9.5;
            if (estimatedLinesSquare > maxLinesBeforeExtend) {
                return 'wide';
            }
            return 'square';
        }

        // --- MOBILE/NON-DESKTOP MODE ---
        // Custom, layout-aware line constraints for 3+ columns mode to prevent text overlap & bleeding
        if (columns && columns >= 3) {
            // Since the columns are much narrower, estimate horizontal characters per line for 3-column formats
            const charsPerLineWide3Col = charsPerLineWide * 0.67;
            const estimatedLinesWide3Col = (length / charsPerLineWide3Col) + newlines;

            // Mobile mode: NEVER square. Defaults to wide, upgrades to wide_tall for longer text.
            if (estimatedLinesWide3Col > 5.5) {
                return 'wide_tall';
            }
            return 'wide';
        }

        // A standard postcard fits roughly 10-11 lines of text vertically.
        // 90% of that is about 9 lines.
        const maxLinesBeforeExtend = 9.5;

        // Estimate lines for wide and square formats. 
        // Square has roughly half the horizontal text capacity of wide.
        const estimatedLinesWide = (length / charsPerLineWide) + newlines;

        // Mobile defaults to wide. Only expands to wide_tall if it exceeds 90% of wide vertical space.
        if (estimatedLinesWide > maxLinesBeforeExtend) {
            return 'wide_tall';
        }
        return 'wide';
    }

    // 2. Explicit shape preferences (user selected)
    if (shape === 'wide' || shape === 'wider') return 'wide';
    if (shape === 'tall' || shape === 'taller' || shape === 'rect') return 'tall';
    if (shape === 'wide_tall') return 'wide_tall';
    if (shape === 'square') return 'square';

    if (postType === 'link') {
        const url = (firstMedia as any)?.url || '';
        const domain = (firstMedia as any)?.domain || '';
        const isSpotify = domain === 'spotify.com' || url.includes('spotify.com');
        const spotifyPlaylistRegex = /\/(playlist|album|artist|show)\//;
        if (isSpotify && spotifyPlaylistRegex.test(url)) {
            return 'wide_tall';
        }
        return 'wide';
    }

    // 3. Explicit square override (for media posts that want to be square)
    if (shape === 'square') return 'square';

    // 4. Fallbacks for missing explicit shape
    if (postType === 'audio' || postType === 'voice') {
        // If an audio post has an explicit cover image with dimensions, use it for aspect ratio
        if (firstMedia?.width && firstMedia?.height) {
            const ratio = firstMedia.width / firstMedia.height;
            if (ratio > 1.2) return 'wide';
            if (ratio < 0.8) return 'tall';
            return 'square';
        }
        return 'square';
    }

    if ((postType === 'video' || postType === 'image') && firstMedia?.width && firstMedia?.height) {
        const ratio = firstMedia.width / firstMedia.height;
        if (ratio > 1.2) return 'wide';
        if (ratio < 0.8) return 'tall';
        return 'square';
    }

    if (postType === 'image') return 'square';

    return 'square'; // Ultimate fallback
};

/**
 * Recommends a postcard shape based on the media content.
 */
export const getRecommendedPostcardShape = (postType: string, firstMedia?: { width?: number; height?: number }, textContent?: string, textSize?: string): 'wide' | 'tall' | 'square' | 'wide_tall' => {
    return getNormalizedPostcardShape(undefined, postType, firstMedia, textContent, textSize);
};

/**
 * Calculates the current footprint daily task cycle date.
 * The cycle rotates exactly at 12:00 PM (noon) Eastern Time.
 */
export const getFootprintCycleDate = (): string => {
    const now = new Date();
    // Get current time in Eastern Time
    const etStr = now.toLocaleString("en-US", { timeZone: "America/New_York" });
    const etDate = new Date(etStr);
    const hour = etDate.getHours();
    
    const cycleDate = new Date(etDate);
    if (hour < 12) {
        cycleDate.setDate(cycleDate.getDate() - 1);
    }
    
    return cycleDate.getFullYear() + '-' + 
           String(cycleDate.getMonth() + 1).padStart(2, '0') + '-' + 
           String(cycleDate.getDate()).padStart(2, '0');
};

/**
 * Returns true if the given ISO timestamp falls within the valid footprint posting
 * window for its cycle day. The window opens at 12:00 PM (noon) Eastern Time on
 * the cycle date and closes at 11:59:59 AM ET the following day (i.e. just before
 * the next cycle's noon flip).
 *
 * This is used when vouches arrive to decide full points vs the 20% late penalty:
 *   - post was timely → award achievement.points (full)
 *   - post was late   → award achievement.points * 0.2
 */
export const wasPostMadeInFootprintWindow = (postedAtIso: string): boolean => {
    if (!postedAtIso) return false;
    try {
        const postUtc = new Date(postedAtIso);
        // Convert the post time to Eastern Time so we can inspect the hour
        const etStr = postUtc.toLocaleString("en-US", { timeZone: "America/New_York" });
        const postEt = new Date(etStr);
        const hour = postEt.getHours();
        // Valid window: 12:00 PM (inclusive) → 11:59 AM next day (exclusive)
        // i.e. hour >= 12  OR  (it's after midnight but before noon, meaning it's
        // still in the same cycle that started at noon yesterday)
        // In other words, the window is: hour >= 12  →  still same calendar day
        //                                hour <  12  →  still in yesterday's cycle window
        // Both of those cases are ALWAYS true for any real post made within the 24 h
        // window, so what we actually need to check is the cycle date the post
        // belonged to vs. the cycle date as-computed by getFootprintCycleDate AT POST TIME.
        //
        // Recompute cycle date at post time:
        const cycleDate = new Date(postEt);
        if (hour < 12) {
            cycleDate.setDate(cycleDate.getDate() - 1);
        }
        const cycleDateStr = cycleDate.getFullYear() + '-' +
            String(cycleDate.getMonth() + 1).padStart(2, '0') + '-' +
            String(cycleDate.getDate()).padStart(2, '0');

        // The window START is noon ET on cycleDate.
        // e.g. cycleDateStr = "2026-06-26" → window opens 2026-06-26 12:00 ET
        const [cy, cm, cd] = cycleDateStr.split('-').map(Number);
        // The post is within the window if:
        //   postEt date === cycleDate  AND  hour >= 12
        //   OR
        //   postEt date === cycleDate + 1 day  AND  hour < 12
        const postDay = postEt.getDate();
        const postMonth = postEt.getMonth() + 1;
        const postYear = postEt.getFullYear();
        const sameDay = postYear === cy && postMonth === cm && postDay === cd;
        const nextDay = (() => {
            const d = new Date(postEt);
            d.setDate(d.getDate() - 1);
            return d.getFullYear() === cy && d.getMonth() + 1 === cm && d.getDate() === cd;
        })();

        if (sameDay && hour >= 12) return true;  // posted in the afternoon/evening of the cycle day
        if (nextDay && hour < 12) return true;   // posted in the early morning after midnight (still same cycle)
        return false;
    } catch {
        return false;
    }
};
